import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { trimTrailingSlash } from "../lib/url";

type Props = {
  selected: string;
  onChange: (value: string, name?: string) => void;
  apiBaseUrl: string;
  getToken: () => Promise<string>;
};

type Patient = {
  patient_id: string;
  name?: string;
};

const PatientSelector: React.FC<Props> = ({ selected, onChange, apiBaseUrl, getToken }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canFetch = Boolean(apiBaseUrl?.trim());

  useEffect(() => {
    if (!canFetch) return;

    const run = async () => {
      setLoading(true);
      setErr(null);

      try {
        const token = await getToken();
        if (!token) throw new Error("No token");

        const resp = await fetch(`${trimTrailingSlash(apiBaseUrl)}/patients`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await resp.json();
        const list: Patient[] = Array.isArray(data?.items) ? data.items : [];

        setItems(list);

        if (!selected && list.length > 0) {
          onChange(list[0].patient_id, list[0].name?.trim() || "");
        }
      } catch (e: any) {
        setErr(e?.message || t("clientSelector.error"));
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [apiBaseUrl, canFetch]);

  const options = useMemo(() => {
    if (loading) return [{ value: "", label: t("clientSelector.loading") }];
    if (err) return [{ value: "", label: t("clientSelector.failedToLoad") }];
    if (!items.length) return [{ value: "", label: t("clientSelector.noClients") }];

    return items.map((p) => ({
      value: p.patient_id,
      label: p.name?.trim() || p.patient_id,
    }));
  }, [items, loading, err, t]);

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <label style={{ fontWeight: 800 }}>{t("clientSelector.label")}</label>

      <select
        className="headerSelect"
        value={selected}
        disabled={loading || !!err || !items.length}
        onChange={(e) => {
          const val = e.target.value;
          const match = items.find((p) => p.patient_id === val);
          onChange(val, match?.name?.trim() || val);
        }}
      >
        {options.map((o, i) => (
          <option key={`${o.value}-${i}`} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// getToken changes reference on every JWT refresh but the component
// only uses it inside the mount-effect — skip it in the shallow compare.
export default React.memo(PatientSelector, (prev, next) =>
  prev.selected === next.selected &&
  prev.apiBaseUrl === next.apiBaseUrl
);
